import React, { FC, useEffect, useRef, useState } from "react";
import { View, ActivityIndicator, KeyboardAvoidingView, Pressable, useWindowDimensions, FlatList, BackHandler, DeviceEventEmitter } from "react-native";
import * as yup from 'yup'
import moment from "moment";
import { useFormik } from "formik";
import Popover from "react-native-popover-view/dist/Popover";
import { useIsFocused } from "@react-navigation/native";

import { config } from "../../shared/environment";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import ZenText from "zen-core-elements/src/elements/zenText/zenText";
import CommonHeader from "../../shared/commonComponents/commonHeader";
import { checkDuplicatesRedux, getDefaultPOSRoles } from "../../redux/slices/commonSlice";
import { appColors, appFonts, fontSize } from "../../themes/appTheme";
import CommonDialogBox from "../../shared/commonComponents/commonDialogBox";
import { CustomerDataProps, CustomerFilterPayloadProps, GetOnePosCustomerGroupResProps, SelectedCustomerProps } from "./customerGroupModule";
import { useAppNavigation, useAppRoute } from "../../types/@react-navigation/navigation";
import { addEditCustomerGroupsStyle } from "../../themes/components/customerGroups/addEditCustomerGroupsStyle";
import { filterItemsProps } from "zen-core-elements/src/elements/zenFilterModal/zenFilterType.modal";
import { ZenBadge, ZenButton, ZenCheckBox, ZenDivider, ZenEmptyComponent, ZenFilterModal, ZenInput, ZenLoader, ZenToast } from "zen-core-elements";
import { createCustomerGroupRedux, getAllPosCustomersRedux, getAllPosMappingCustomersRedux, getOnePosCustomerGroupRedux, resetCustomersCache, updateCustomerGroupRedux } from "../../redux/slices/customerGroupSlice";
import { useTheme } from "@rneui/themed";
import { ListToolbar } from "../../shared/commonComponents/listToolbar";
import { webScrollbarProps } from "../../platform/design/scrollWeb";
import { CustomModal } from "../../shared/commonComponents/customModal";

//Method to convert a customer into the shape stored for a customer group.
//Accepts a customer list row (details nested under `user`) or an already mapped customer (flat, from the group mapping api)
export const toSelectedCustomer = (customer: CustomerDataProps | SelectedCustomerProps): SelectedCustomerProps => {
    const source: any = customer
    const user: any = source?.user ?? source
    const isWholesale = Boolean(source?.isWholesale)
    const isApproved = source?.isApproved ?? null
    const firstName = user?.firstName ?? ""
    const lastName = user?.lastName ?? ""
    return {
        firstName,
        lastName,
        name: user?.name || `${firstName} ${lastName}`.trim(),
        email: user?.email ?? "",
        // In a list row `userId` is the numeric users table id, the uuid lives on the user object
        userId: typeof source?.userId === "string" ? source.userId : (user?.userId ?? user?.id ?? ""),
        usersId: source?.usersId ?? (typeof source?.userId === "number" ? source.userId : null),
        phone: user?.phone ?? "",
        countryCode: source?.countryCode,
        isWholesale,
        isApproved,
        profileName: source?.profileName ?? user?.profileName ?? null,
        customerType: source?.customerType ?? (isWholesale ? "Wholesale" : "Customer"),
        wholesaleStatus: source?.wholesaleStatus ?? (!isWholesale ? "N/A" : isApproved === true ? "Approved" : isApproved === false ? "Declined" : "Pending"),
        customerId: source?.customerId
    }
}

const AddEditCustomerGroups: FC = () => {
    //State variable to use useIsFocused hook to notify the page is focused or not
    const isFocused = useIsFocused()
    //State variable to use useAppDispatch hook
    const dispatch = useAppDispatch()
    const { theme } = useTheme()
    // ─── Redux state ────────────────────────────────────────────────────────────
    const { customerGroupDetails, allPosCustomers, mappingCustomers } = useAppSelector((state) => state.customerGroup)
    const { loading: checkDuplicateLoading, value: checkDuplicateValue } = useAppSelector(state => state?.common?.checkDuplicate)
    const defaultPosRoles = useAppSelector((state) => state.common.defaultRoles)
    // Derived lists from Redux cache
    const customerData: CustomerDataProps[] = allPosCustomers.value?.customers?.rows ?? []
    const customerMappingData: CustomerDataProps[] = mappingCustomers.value?.customersList?.rows ?? []

    //State variable to use width measurement from useWindowDimensions hook
    const { width, height } = useWindowDimensions()
    //State variable to use useNavigation hook to navigate between pages
    const navigation = useAppNavigation()
    //State variable to use useRoute hook to get params from parent component
    const route = useAppRoute("AddEditCustomerGroups")
    //Variable to store isEdit value from parent component
    const isEdit = route?.params?.isEdit
    //Variable used to store isAdd value from parent component
    const isAdd = route?.params?.isAdd
    //Variable to store isView value from parent component
    const isView = route?.params?.isView
    //Variable to store id value from parent component
    const isEditId = route?.params?.id ? route?.params?.id : null
    const [dupName, setDupName] = useState('')
    //variable to store filter count
    const [filtercount, setFilterCount] = useState(0)
    //State variable to store boolean value for page loading (getOne call)
    const [isLoading, setIsLoading] = useState<boolean>(isView || isEdit ? true : false)
    //State variable to store boolean value for filter popover
    const [isVisibleFilter, setIsVisibleFilter] = useState<boolean>(false)
    //State variable to store customer list data to display as selected/to create the selected customer list into group
    const [selectedCustomerData, setSelectedCustomerData] = useState<SelectedCustomerProps[]>([])
    //State variable to store customer list id to display as selected/to create the selected customer list into group
    const [selectedCustomerId, setSelectedCustomerId] = useState<number[]>([])
    console.log(selectedCustomerData, "selectedCustomerData");

    //State variable to store search text value
    const [searchText, setSearchText] = useState<string>()
    //State variable to store boolean value for isDirty
    const [isDirty, setIsDirty] = useState<boolean>(false)
    //Variable to maintain confirmation popup state while leaving the page
    const [isLeavePopover, setIsLeavePopover] = useState(false)
    //variable used to store customer grp data
    const [oneCustomerGroupData, setOneCustomerGroupData] = useState<GetOnePosCustomerGroupResProps["customerGroupDetails"] | null>(null)
    const [transparentLoader, setTransparentLoader] = useState(false)

    const yupValidation = yup.object().shape({
        from: yup.number()
            .typeError("Enter valid Input")
            .nullable()
            .transform((value, originalValue) =>
                originalValue.trim() === "" ? null : value
            )
            .min(
                0,
                `From range should start from 0`
            )
        ,
        to: yup.number()
            .typeError("Enter valid Input")
            .nullable()
            .transform((value, originalValue) =>
                originalValue.trim() === "" ? null : value
            )
            .min(1, `To range should start from 1`)
            .when("from", (from, schema) => {
                return from && from[0] ?
                    schema.moreThan(
                        from[0],
                        "To range must be greater than From range"
                    ).required("This field is required") :
                    schema.test(
                        '',
                        'From is required',
                        (to) => {
                            return !from[0] && to ? false : true
                        }
                    )
            }
            ),
    })

    //variable to store filter payload
    const [filterData, setFilterData] = useState<filterItemsProps[]>([
        {
            id: "1",
            filterName: "Created Date",
            filterType: "dateRange",
            initialSelected: { startDate: "", endDate: "" },
            filterValue: { minDate: "", maxDate: "" }
        },
        {
            id: '2',
            filterName: 'Medium',
            filterType: "radio",
            filterValue: [
                {
                    value: 'WEB',
                    label: 'Admin',
                },
                {
                    value: 'POS',
                    label: 'POS',
                }
            ],
            initialSelected: ''
        },
        {
            id: '3',
            filterName: 'Status',
            filterType: "radio",
            filterValue: [
                {
                    value: 'Active',
                    label: 'Active',
                },
                {
                    value: 'Pending',
                    label: 'Pending',
                }
            ],
            initialSelected: ''
        },
        {
            id: '4',
            filterName: 'Customer Type',
            filterType: "radio",
            filterValue: [],
            initialSelected: ''
        },
        // {
        //     id: '5',
        //     filterName: 'Loyalty Amount Range',
        //     filterType: "inputRange",
        //     input: { keyboardType: "number-pad" },
        //     initialSelected: { from: "", to: "" },
        //     validation: yupValidation
        // },
        // {
        //     id: '6',
        //     filterName: 'On-account Limit Range',
        //     filterType: "inputRange",
        //     input: { keyboardType: "number-pad" },
        //     initialSelected: { from: "", to: "" },
        //     validation: yupValidation
        // },
    ])

    //variable to maintain offset
    const [offset, setOffset] = useState<number>(0);
    //variable to maintain limit
    const limit = 10
    //variable to store filter payload
    const [filterPayLoad, setFilterPayLoad] = useState<CustomerFilterPayloadProps>(null)

    // Ref to prevent multiple onEndReached fires
    const onMomentumScrollBegin = useRef(false)
    const prevSearchTextRef = useRef<string | undefined>(undefined)

    // ─── Helpers ──────────────────────────────────────────────────────────────

    //Method used to perform leave confirmation
    const leaveConfirmation = (value: { name: string }) => {
        if (value?.name === 'Leave Page') {
            navigation.goBack();
            customerFormik?.resetForm()
        }
        else setIsLeavePopover(false)
    };

    const reInitializeStates = () => {
        setFilterPayLoad(null)
        setFilterCount(0)
        DeviceEventEmitter?.emit("customerGroupFilter")
        setSearchText("")
    }

    //UseEffect used set payload data when Add mode
    useEffect(() => {
        if (isAdd) {
            setSelectedCustomerData([])
            setSelectedCustomerId([])
            setOffset(0)
            // Reset Redux cache so fresh data is always fetched for add mode
            dispatch(resetCustomersCache())
            reInitializeStates()
        }
    }, [isAdd])

    //UseEffect used to call getAllPosCustomers api on offset change (pagination)
    useEffect(() => {
        if (offset === 0) return  // handled by search/filter/focus effects
        getAllPosCustomers(offset)
    }, [offset])

    //UseEffect to render the when the focused value changes
    useEffect(() => {
        if (isFocused && (isView || isEdit)) {
            setIsLoading(true);
            getOnePosCustomerGroup(route?.params?.id)
        }
        if (isFocused && isAdd) {
            // Always fetch fresh list for add mode
            getAllPosCustomers(0, true)
        }
    }, [isFocused]);

    //UseEffect to render the when the searchText value changes
    useEffect(() => {
        if (prevSearchTextRef.current === undefined) {
            prevSearchTextRef.current = searchText
            return
        }
        if (prevSearchTextRef.current === searchText) return
        prevSearchTextRef.current = searchText

        const searchDebounce = setTimeout(() => {
            getAllPosCustomers(0, true)
        }, 1000)
        return () => { clearTimeout(searchDebounce) }
    }, [searchText])

    //UseEffect to handle filter selection
    useEffect(() => {
        if (filterPayLoad !== null) {
            getAllPosCustomers(0, true)
        }
    }, [filterPayLoad])

    const convertCapitalLetter = (str: string) => {
        return str[0].toUpperCase() + str.slice(1)
    }

    useEffect(() => {
        if (defaultPosRoles) {
            setFilterData((prev) => {
                let data = [...prev]
                if (data[3].filterType == "radio" && !data[3].filterValue?.length) {
                    defaultPosRoles.forEach((item) => {
                        data[3].filterType == "radio" && data[3].filterValue.push({
                            value: item.id.toString(),
                            label: convertCapitalLetter(item?.name)
                        })
                    })
                    data[3].filterType == "radio" && data[3].filterValue.push({
                        value: "-1",
                        label: "Wholesale customer"
                    })
                }
                return [...data]
            })
        }
    }, [defaultPosRoles])

    const openFilterModal = () => {
        if (!defaultPosRoles) {
            setTransparentLoader(true)
            dispatch(getDefaultPOSRoles({ storeId: config.storeId })).unwrap().then((response) => {
                if (response?.success) {
                    setIsVisibleFilter(true)
                } else {
                    ZenToast({ type: "error", message: "Failed to get a default roles" })
                }
            }).catch((error) => {
                ZenToast({ type: "error", message: "Failed to get a default roles" })
            }).finally(() => {
                setTransparentLoader(false)
            })
        } else {
            setIsVisibleFilter(true)
        }
    }

    //Variable for validating formik values
    const validationSchema = yup.object().shape({
        groupName: yup.string()
            .required("Group name is required")
            .label("groupName"),
        description: yup.string()
            .label("description")
            .nullable(),
    })

    //Variable to store initial value, validation schema and onSubmit call back from UseFormik hook
    const customerFormik = useFormik({
        enableReinitialize: true,
        validateOnMount: true,
        initialValues: {
            groupName: oneCustomerGroupData?.groupName ? oneCustomerGroupData?.groupName : '',
            description: oneCustomerGroupData?.description ? oneCustomerGroupData?.description : null,
        },
        validationSchema: validationSchema,
        onSubmit: () => {
            isEdit ? updateCustomerGroup(customerGroupDetails?.id) : createCustomerGroup()
        }
    })

    //UseEffect function which is used to perform backhandler operation
    useEffect(() => {
        BackHandler.addEventListener('hardwareBackPress', function () {
            if (customerFormik?.dirty)
                setIsLeavePopover(true)
            else
                navigation.goBack()
            return true
        })
    }, [customerFormik?.dirty])

    //UseEffect to render the when the formik value group name changes
    useEffect(() => {
        if (dupName) {
            const nameDebounceFunction = setTimeout(async () => {
                if ((dupName != null || dupName != '') && !isEdit) {
                    const payload = {
                        storeId: config.storeId,
                        data: {
                            columnName: "groupName",
                            id: isEditId ? isEditId : null,
                            storeId: true,
                            tableName: "customerGroups",
                            value: dupName
                        }
                    }
                    await dispatch(checkDuplicatesRedux(payload))?.unwrap()?.catch((err) => {
                        console.log('Error in check duplicate', err)
                    })
                }
            }, 1000)
            return () => clearTimeout(nameDebounceFunction)
        }
    }, [dupName])

    //Method to dispatch createCustomerGroup api call
    const createCustomerGroup = async () => {
        let payload = {
            storeId: config.storeId,
            data: {
                customerGroup: customerFormik.values,
                customers: selectedCustomerData,
            }
        }

        await dispatch(createCustomerGroupRedux(payload))?.unwrap()?.then((createResponse) => {
            if (createResponse?.createCustomerGroup[1]) {
                ZenToast({
                    type: "success", message: "Customer Group created successfully"
                })
                navigation?.goBack()
            }
        }).catch((error) => {
            console.log(error);

            ZenToast({
                type: "error",
                message: "Error while creating customer group"
            })
        })
    };

    //Method to dispatch getOnePosCustomerGroup api call
    const getOnePosCustomerGroup = async (id: number | undefined) => {
        let payload = {
            storeId: config.storeId,
            id: id
        }
        await dispatch(getOnePosCustomerGroupRedux(payload))?.unwrap()?.then((oneCustomerResponse) => {
            if (oneCustomerResponse?.customerGroupDetails?.groupName) {
                setOneCustomerGroupData(oneCustomerResponse?.customerGroupDetails)
                if (oneCustomerResponse?.customerGroupDetails?.id && isView) {
                    isView && getAllPosMappingCustomers(oneCustomerResponse?.customerGroupDetails?.id)
                }
                if (isEdit) {
                    // Reset cache and fetch fresh customer list in edit mode
                    dispatch(resetCustomersCache())
                    setOffset(0)
                    getAllPosCustomers(0, true)

                    let mappingPayload = {
                        storeId: config.storeId,
                        customerGroupId: oneCustomerResponse.customerGroupDetails.id,
                        data: {
                            limit: null,
                            offset: 0,
                            searchText: '',
                            storeId: config.storeId,
                            customerGroupId: oneCustomerResponse.customerGroupDetails.id
                        }
                    }
                    dispatch(getAllPosMappingCustomersRedux(mappingPayload))?.unwrap()?.then((res) => {
                        const mappedRows = (res?.customersList?.rows || []).map(toSelectedCustomer)
                        setSelectedCustomerData(mappedRows)
                        console.log(mappedRows, "mappedRows");

                        setSelectedCustomerId(mappedRows?.map((customer) => customer?.userId))
                    })
                }
                setIsLoading(false)
            }
        }).catch(() => {
            ZenToast({
                type: "error",
                message: "Error while getting one pos customer detail"
            })
        })
    }

    //Method to dispatch getAllPosMappingCustomers api call (view mode)
    const getAllPosMappingCustomers = async (customerGroupId: number | null, search?: string) => {
        console.log("dddddddddddd");

        let payload = {
            storeId: config.storeId,
            customerGroupId: customerGroupId,
            data: {
                limit: null,
                offset: 0,
                searchText: search ? search : '',
                storeId: config.storeId,
                customerGroupId: customerGroupId
            }
        }
        await dispatch(getAllPosMappingCustomersRedux(payload))?.unwrap()?.catch(() => {
            ZenToast({
                type: "error",
                message: "Error while getting pos mapping customers"
            })
        })
        setIsLoading(false)
    }

    //Method to dispatch updateCustomerGroup api call
    const updateCustomerGroup = async (customerGroupId: number | null) => {
        let payload = {
            storeId: config.storeId,
            customerGroupId: customerGroupId,
            data: {
                customerGroup: customerFormik.values,
                customers: selectedCustomerData,
            }
        }
        await dispatch(updateCustomerGroupRedux(payload))?.unwrap()?.then((updateResponse) => {
            if (updateResponse?.updateCustomerGroup) {
                ZenToast({
                    type: "success", message: "Customer Group updated successfully"
                })
                setSelectedCustomerData([]);
                navigation?.goBack()
            }
        }).catch((err) => {
            ZenToast({
                type: "error",
                message: "Error while updating customer group"
            })
        })
    }

    /**
     * Fetch customers from the API — cache-aware.
     * @param currentOffset  Page offset (0 = first page)
     * @param forceRefresh   Skip cache check and always fetch
     */
    const getAllPosCustomers = (currentOffset: number = 0, forceRefresh = false) => {

        if (allPosCustomers.loading) return

        let filterDataStr: string | null = null
        let isWholeSaleCustomer = false

        if (filterPayLoad) {
            let tempData = { ...filterPayLoad }
            isWholeSaleCustomer = tempData.isWholeSale || false
            if (isWholeSaleCustomer) {
                delete tempData.isWholeSale
            }
            filterDataStr = JSON.stringify(tempData)
        }

        // Cache check — skip if same filter & first page & we already have data
        if (!forceRefresh && currentOffset === 0) {
            const currentFilter = JSON.stringify({
                searchText: searchText ? searchText?.trim() : "",
                filterData: filterDataStr
            })

            if (allPosCustomers.filter === currentFilter && allPosCustomers.value) return
        }

        let payloadData = {
            storeId: config.storeId,
            data: {
                offset: currentOffset,
                limit: limit,
                isActive: true,
                searchText: searchText ? searchText?.trim() : "",
                filterData: filterDataStr,
                storeId: config?.storeId,
                ...(isWholeSaleCustomer && { isWholesale: true })
            }
        }
        dispatch(getAllPosCustomersRedux(payloadData))?.unwrap()?.catch(() => {
            ZenToast({
                type: "error",
                message: "Error while getting pos customers"
            })
        })
    }



    //Method for selecting and deselecting customer data from the list displayed
    const selectCard = (data: CustomerDataProps) => {
        const ind = selectedCustomerData.findIndex((selectedItem) => selectedItem?.customerId === data.customerId);
        if (ind === -1) {
            setSelectedCustomerId([...selectedCustomerId, data?.customerId])
            setSelectedCustomerData([...selectedCustomerData, toSelectedCustomer(data)]);
        } else {
            const updatedSelectedItems = [...selectedCustomerData];
            const updatedSelectedIdItems = [...selectedCustomerId];
            updatedSelectedItems.splice(ind, 1);
            updatedSelectedIdItems.splice(ind, 1);
            setSelectedCustomerData(updatedSelectedItems);
            setSelectedCustomerId(updatedSelectedIdItems)
        }
    }

    //Method for checking the page is dirty or not
    const checkIsDirty = () => {
        if (isEdit) {
            if (customerFormik.values?.groupName !== customerGroupDetails.groupName || customerFormik.values?.description !== customerGroupDetails.description || isDirty) {
                setIsLeavePopover(true)
            }
            else {
                navigation.goBack()
            }
        }
        else if (isView) {
            navigation.goBack()
        }
        else {
            (customerFormik?.dirty || isDirty || selectedCustomerData?.length > 0) ? setIsLeavePopover(true) : navigation.goBack()
        }
    }

    //Method for pagination in FlatList
    const onEndReached = () => {
        if (!allPosCustomers.isReached && !allPosCustomers.loading && customerData.length > 0) {
            if (!onMomentumScrollBegin.current) return
            onMomentumScrollBegin.current = false
            const newOffset = offset + limit
            setOffset(newOffset)
            getAllPosCustomers(newOffset)
        }
    }
    console.log(selectedCustomerId, "selectedCustomerId");

    const renderItem = ({ item, index }: { item: CustomerDataProps, index: number }) => {
        return (
            <View key={index} style={[addEditCustomerGroupsStyle.listContainer, {
                borderWidth: selectedCustomerId?.includes(item?.customerId) ? 1 : 0,
                borderColor: selectedCustomerId?.includes(item?.customerId) ? appColors.primary : appColors.light, marginTop: 8
            }]}>
                <Pressable onLongPress={() => {
                    height > width || !isView ? selectCard(item) : null
                    setIsDirty(true)
                }}
                    onPress={() => {
                        selectedCustomerId?.length ? width > height || !isView ? selectCard(item) : null : null
                    }}
                    style={{
                        flexDirection: 'row',
                    }}>
                    {
                        isView ? null :
                            width > 600 ?
                                <View style={{ justifyContent: 'center', alignSelf: 'center', right: 5, }}>
                                    <ZenCheckBox
                                        size={width > 600 ? 22 : 18}
                                        containerStyle={{ margin: 0, padding: width > 600 ? 5 : 0, marginHorizontal: 5 }}
                                        checkedColor={appColors.primary}
                                        onPress={() => {
                                            selectCard(item)
                                        }}
                                        checked={selectedCustomerId?.includes(item?.customerId)} />
                                </View>
                                : null
                    }
                    <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                            <ZenText content={item?.user?.name ? item?.user?.name : item?.name} style={{ fontSize: fontSize?.mediumFontSize }} bold={true} color={appColors.primary} />
                            {item.user?.createdAt &&
                                <ZenBadge
                                    badgeName={moment(item.user?.createdAt).format('DD-MMM-YYYY')}
                                    badgeColor={appColors?.lightYellow}
                                    badgeTextColor={appColors?.lightDark}
                                    badgeTextStyle={addEditCustomerGroupsStyle.textStyle}
                                    badgeContainerStyle={[addEditCustomerGroupsStyle.containerStyle,]}
                                />
                            }
                        </View>
                        <ZenDivider style={{ marginVertical: 5 }} />
                        {
                            width > height ?
                                <View style={addEditCustomerGroupsStyle.flexDirection}>
                                    <View style={{ flex: width > 900 ? 0.2 : 0.3 }}>
                                        <View style={addEditCustomerGroupsStyle.flexDirection}>
                                            <ZenText content="Medium: " style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} color={appColors.labelColor} />
                                            <ZenText content={item.medium} style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} bold={true} color={appColors.lightDark} />
                                        </View>
                                        <View style={addEditCustomerGroupsStyle.flexDirection}>
                                            <ZenText content="Total Spend: " style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} color={appColors.labelColor} />
                                            <ZenText content={item.totalSpent ? String(item.totalSpent) : '0'} style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} bold={true} color={appColors.lightDark} />
                                        </View>
                                    </View>
                                    <View style={{ flex: width > 900 ? 0.2 : 0.3 }}>
                                        <View style={addEditCustomerGroupsStyle.flexDirection}>
                                            <ZenText content="Status: " style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} color={appColors.labelColor} />
                                            <ZenText content={item?.user?.isActive == true ? 'Active' : 'In-active'} style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} bold={true} color={appColors.lightDark} />
                                        </View>
                                        {/* <View style={addEditCustomerGroupsStyle.flexDirection}>
                                            <ZenText content="Loyalty: " style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} color={appColors.labelColor} />
                                            <ZenText content={item?.balancePoints ? String(item.balancePoints) : '0'} style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} bold={true} color={appColors.lightDark} />
                                        </View> */}
                                    </View>
                                    <View style={{ flex: width > 900 ? 0.2 : 0.3 }}>
                                        <View style={addEditCustomerGroupsStyle.flexDirection}>
                                            <ZenText content="Customer Type: " style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} color={appColors.labelColor} />
                                            <ZenText content={item?.isWholesale ? 'Wholesale' : 'Customer'} style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} bold={true} color={appColors.lightDark} />
                                        </View>
                                        {/* <View style={addEditCustomerGroupsStyle.flexDirection}>
                                            <ZenText content="On-account: " style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} color={appColors.labelColor} />
                                            <ZenText content={item?.onAccountBalance ? String(item.onAccountBalance) : '0'} style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} bold={true} color={appColors.lightDark} />
                                        </View> */}
                                    </View>
                                </View>
                                :
                                <View style={{ flexDirection: 'row', flex: 1 }}>
                                    <View style={{ flex: 0.5 }}>
                                        <View style={addEditCustomerGroupsStyle.flexDirection}>
                                            <ZenText content="Medium: " style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} color={appColors.labelColor} />
                                            <ZenText content={item?.medium} style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} bold={true} color={appColors.lightDark} />
                                        </View>
                                        <View style={addEditCustomerGroupsStyle.flexDirection}>
                                            <ZenText content="Total Spend: " style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} color={appColors.labelColor} />
                                            <ZenText content={item?.totalSpent ? String(item?.totalSpent) : '0'} style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} bold={true} color={appColors.lightDark} />
                                        </View>
                                        <View style={addEditCustomerGroupsStyle.flexDirection}>
                                            <ZenText content="Status: " style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} color={appColors.labelColor} />
                                            <ZenText content={item?.user?.isActive || item?.isActive ? 'Active' : item?.isApproved ? 'Pending' : 'In-active'} style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} bold={true} color={appColors.lightDark} />
                                        </View>
                                    </View>
                                    <View style={{ flex: 0.5 }}>
                                        <View style={addEditCustomerGroupsStyle.flexDirection}>
                                            <ZenText content="Loyalty: " style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} color={appColors.labelColor} />
                                            <ZenText content={item?.balancePoints ? String(item?.balancePoints) : '0'} style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} bold={true} color={appColors.lightDark} />
                                        </View>
                                        <View style={addEditCustomerGroupsStyle.flexDirection}>
                                            <ZenText content="Type: " style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} color={appColors.labelColor} />
                                            <ZenText content={item?.isWholesale ? 'Wholesale' : 'Customer'} style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} bold={true} color={appColors.lightDark} />
                                        </View>
                                        <View style={addEditCustomerGroupsStyle.flexDirection}>
                                            <ZenText content="On-account: " style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} color={appColors.labelColor} />
                                            <ZenText content={item?.onAccountBalance ? String(item?.onAccountBalance) : '0'} style={[addEditCustomerGroupsStyle.customerListHeaderText, { fontSize: fontSize?.smallHeaderFontSize }]} bold={true} color={appColors.lightDark} />
                                        </View>
                                    </View>
                                </View>
                        }
                    </View>
                </Pressable>
            </View>
        )
    }

    // Footer shows a spinner while more pages are loading
    const ListFooterComponent = () => {
        return (
            <View>
                {!allPosCustomers.isReached && customerData.length > 0 &&
                    <View style={{ paddingTop: 10, paddingBottom: 5 }}>
                        <ActivityIndicator size={"small"} color={appColors.primary} />
                    </View>
                }
            </View>
        )
    }

    // Whether the list section is loading (first-page fetch, no data yet)
    const isListLoading = allPosCustomers.loading && customerData.length === 0
    const isMappingLoading = mappingCustomers.loading && customerMappingData.length === 0

    return (
        <KeyboardAvoidingView style={addEditCustomerGroupsStyle.containerView}>
            <CommonHeader backIcon backIconOnpress={() => { checkIsDirty() }} headerName={isEdit ? 'Edit Customer Group' : isView ? 'View Customer Group' : 'Create Customer Group'} rightComponent={!isView ? <ZenButton
                title="Save"
                disabledStyle={{ borderColor: 'transparent' }}
                // disabled={(Object.keys(customerFormik?.errors)?.length > 0 || checkDuplicateValue?.result?.alreadyExists || (!customerFormik?.dirty && selectedCustomerData.length === 0) || isView) ? true : false}
                containerStyle={{ marginHorizontal: 5 }}
                buttonStyle={[addEditCustomerGroupsStyle.floatingButtonStyle, { backgroundColor: appColors.primary, }]}
                titleStyle={{ color: appColors.light, fontSize: fontSize.smallHeaderFontSize, fontFamily: appFonts?.bold }}
                onPress={() => {
                    if (route?.params?.customerCount == selectedCustomerData?.length && !customerFormik?.dirty) {
                        ZenToast({ type: "info", message: "You have no changes to save." })
                    } else {
                        customerFormik.handleSubmit();
                    }
                }} /> : undefined} />
            <View style={addEditCustomerGroupsStyle.floatingButtonContainer}>
                {/* <ZenButton
                    title="Cancel"
                    containerStyle={{ marginHorizontal: 5 }}
                    buttonStyle={[addEditCustomerGroupsStyle.floatingButtonStyle, { backgroundColor: appColors.light, borderColor: appColors.primary, borderWidth: 0.5 }]}
                    titleStyle={{ color: appColors.primary, fontSize: fontSize.smallHeaderFontSize, fontFamily: appFonts?.bold }}
                    onPress={() => { checkIsDirty() }}
                /> */}

            </View>
            {
                isLoading ?
                    <ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={appColors?.primary} lottieType="Local" lottie={require('../../assets/infinityLoader.json')} />
                    :
                    <View style={addEditCustomerGroupsStyle.viewStyle}>
                        <ZenInput
                            type="outlined"
                            label="Group Name *"
                            disabled={isView}
                            onChangeText={(text: string) => {
                                customerFormik.setFieldValue('groupName', text)
                                setDupName(text)
                            }}
                            onBlur={() => {
                                customerFormik.setFieldTouched('groupName', true)
                                customerFormik.setFieldValue('groupName', customerFormik?.values.groupName?.trim())
                            }}
                            errorMessage={(customerFormik?.touched.groupName && customerFormik?.errors.groupName) ? (customerFormik?.errors.groupName as string) : checkDuplicateValue?.result?.alreadyExists ? 'Group name already exists!' : undefined}
                            value={customerFormik?.values.groupName}
                            inputContainerStyle={{ paddingHorizontal: 8 }}
                            rightIcon={checkDuplicateLoading ? <ZenLoader size={"small"} color={appColors.primary} /> : null}
                            containerStyle={{ marginTop: 10 }}
                            inputStyle={{ height: 40 }}
                            errorStyle={{ fontSize: fontSize.errorFontSize, fontFamily: appFonts.medium, color: appColors.red, paddingBottom: (customerFormik?.errors?.groupName && !customerFormik?.isValid && customerFormik?.touched.groupName) || checkDuplicateValue?.result?.alreadyExists ? 20 : 0 }}
                        />

                        <ZenInput
                            value={customerFormik?.values.description!}
                            disabled={isView}
                            onChangeText={(text: string) => { customerFormik.setFieldValue('description', text) }}
                            onBlur={() => {
                                customerFormik.setFieldTouched('description', true)
                                customerFormik.setFieldValue('description', customerFormik?.values.description?.trim())
                            }}
                            type="outlined"
                            inputContainerStyle={{ paddingHorizontal: 8 }}
                            inputStyle={{ height: 80 }}
                            label="Description"
                            errorMessage={
                                customerFormik?.touched.description && customerFormik?.errors.description
                                    ? customerFormik?.touched.description && customerFormik?.errors.description
                                    : undefined
                            }
                            errorStyle={[
                                {
                                    fontSize: fontSize.errorFontSize,
                                    fontFamily: appFonts.medium,
                                    color: appColors.red,
                                    paddingBottom:
                                        customerFormik?.errors?.description &&
                                            !customerFormik?.isValid &&
                                            customerFormik?.touched.description
                                            ? 20
                                            : 0,
                                },
                            ]}
                            containerStyle={{ marginTop: -5 }}
                        />

                        <View style={[addEditCustomerGroupsStyle.subViewStyle, { paddingHorizontal: width > 600 ? 10 : undefined, flex: 1 }]}>
                            <ZenText content="All Customers" color={appColors.primary} bold={true} style={{ fontSize: fontSize?.mediumFontSize, marginHorizontal: 25, marginTop: 10, }} />
                            <View style={{ backgroundColor: theme?.lightBackgroundColor }}>
                                <ListToolbar
                                    search={{
                                        value: searchText ?? "",
                                        onChange: (text) => setSearchText(text as string),
                                        onCancel: () => {
                                            setSearchText("")
                                        },
                                        placeholder: "Search"
                                    }}
                                    filter={{
                                        count: filtercount ?? 0,
                                        onPress: openFilterModal
                                    }}
                                />
                            </View>
                            {
                                (isView ? isMappingLoading : isListLoading) ?
                                    <ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={appColors?.primary} lottieType="Local" lottie={require('../../assets/infinityLoader.json')} />
                                    :
                                    <View style={{ flex: 1 }}>
                                        {
                                            selectedCustomerData?.length && !isView ?
                                                <View style={{ marginHorizontal: 10, marginTop: 10 }}>
                                                    <ZenCheckBox
                                                        size={width > 600 ? 22 : 18}
                                                        containerStyle={{ marginVertical: 0, paddingVertical: 0, backgroundColor: "transparent" }}
                                                        checkedColor={appColors.primary}
                                                        titleStyle={{ color: appColors?.lightDark, fontSize: fontSize?.smallerFontSize, paddingLeft: 5 }}
                                                        title={selectedCustomerData?.length == customerData?.length ? 'Deselect All' : 'Select All'}
                                                        onPress={() => {
                                                            setIsDirty(true)
                                                            if (selectedCustomerData?.length == customerData?.length) {
                                                                setSelectedCustomerData([])
                                                                setSelectedCustomerId([])
                                                            }
                                                            else {
                                                                setSelectedCustomerData(customerData?.map(toSelectedCustomer))
                                                                let dataId: number[] = []
                                                                customerData?.forEach((val) => {
                                                                    dataId.push(val?.customerId)
                                                                })
                                                                setSelectedCustomerId(dataId)
                                                            }
                                                        }}
                                                        checked={selectedCustomerId?.length == customerData?.length ? true : false} />
                                                </View>
                                                : null
                                        }
                                        <FlatList
                                            data={isView ? customerMappingData : customerData}
                                            renderItem={renderItem}
                                            keyExtractor={(item, index) => String(index)}
                                            onMomentumScrollBegin={() => { onMomentumScrollBegin.current = true }}
                                            onEndReached={onEndReached}
                                            onEndReachedThreshold={0.5}
                                            ListFooterComponent={isView ? null : ListFooterComponent}
                                            ListEmptyComponent={ZenEmptyComponent}
                                            showsVerticalScrollIndicator={false}
                                            {...webScrollbarProps}
                                        />
                                    </View>
                            }
                        </View>
                    </View>
            }
            <Popover isVisible={isLeavePopover} popoverStyle={{ width: width > 900 ? 600 : width > 600 ? 500 : 300, borderRadius: 10 }}>
                <CommonDialogBox type="EDIT" replaceValue="" confirmation={leaveConfirmation} />
            </Popover>
            <ZenFilterModal
                filterName="customerGroupFilter"
                filterItems={filterData}
                setFilterItems={setFilterData}
                visible={isVisibleFilter}
                setVisible={setIsVisibleFilter}
                filterCount={filtercount}
                setFilterCount={setFilterCount}
                isSingleExpand={false}
                onConfirm={(val) => {
                    let data: CustomerFilterPayloadProps = {}
                    if (val?.values?.["Medium"]) {
                        data["medium"] = val?.values?.["Medium"]
                    }
                    if (val?.values?.["Created Date"] && val?.values?.["Created Date"]?.startDate && val?.values?.["Created Date"]?.endDate) {
                        data["customDateRange"] = { begin: val?.values?.["Created Date"]?.startDate, end: val?.values?.["Created Date"]?.endDate }
                    }
                    if (val.values.Status) {
                        data.status = val.values.Status
                    }
                    if (val.values?.["Customer Type"]) {
                        const customerType = val.updatedFilters[3]
                        if (customerType.filterType == "radio") {
                            customerType.filterValue?.forEach((item) => {
                                if (item?.value == customerType?.initialSelected) {
                                    if (item?.label == "Wholesale customer") {
                                        let wholesaleData = customerType.filterValue.filter((subItem) => subItem?.label === "Customer")
                                        data.roleId = Number(wholesaleData[0].value)
                                        data.type = wholesaleData[0].label
                                        data.isWholeSale = true
                                    }
                                    else {
                                        data.roleId = Number(item?.value)
                                        data.type = item?.label?.split(" ")[0]
                                    }
                                }
                            })
                        }
                    }
                    let loyaltyRange = val?.values?.['Loyalty Amount Range']
                    if (loyaltyRange && loyaltyRange?.from && loyaltyRange?.to) {
                        data.loyaltyRange = { start: loyaltyRange?.from, end: loyaltyRange?.to }
                    }
                    let accountRange = val?.values?.['On-account Limit Range']
                    if (accountRange && accountRange?.from && accountRange?.to) {
                        data.accountLimitRage = { start: accountRange?.from, end: accountRange?.to }
                    }
                    setFilterPayLoad(data)
                }} title="Customer List Filter" />
            <CustomModal visible={transparentLoader} transparent>
                <ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={appColors?.primary} lottieType="Local" lottie={require('../../assets/infinityLoader.json')} />
            </CustomModal>
        </KeyboardAvoidingView>

    )
}
export default AddEditCustomerGroups